import React, { useState, useEffect } from 'react';
import ActionPlaceholder from '../../common/ActionPlaceholder';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { listSupporters } from '../../../api/supporters';

// Keep in sync with MAX_EPK_SUPPORTERS in server/models/fepk.js. The server
// silently truncates anything past this, so the picker stops the user first
// rather than letting a save quietly drop their choices.
const MAX_SUPPORTERS = 24;

const getSafeId = (obj) => {
  if (!obj) return "";
  if (typeof obj === 'string') return obj;
  if (obj._id) return String(obj._id);
  if (obj.id) return String(obj.id);
  return "";
};

const SupporterPickerModal = ({ selected, onConfirm, onClose }) => {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [currentSelected, setCurrentSelected] = useState(selected || []);

  useEffect(() => {
    listSupporters(false).then((data) => {
      setCatalog(data || []);
    }).catch((err) => {
      console.error(err);
    });
  }, []);

  const toggleSupporter = (supporter) => {
    const id = getSafeId(supporter);
    const isSelected = currentSelected.find(s => getSafeId(s) === id);
    if (isSelected) {
      setCurrentSelected(currentSelected.filter(s => getSafeId(s) !== id));
    } else {
      if (currentSelected.length >= MAX_SUPPORTERS) return;
      setCurrentSelected([...currentSelected, supporter]);
    }
  };

  const IMAGE_URL_PREFIX = `${process.env.REACT_APP_AWS_URL}`;

  return (
    // z-[1060] clears the sticky EditNavBar at z-[1050], same as the validation
    // modal in EpkViewPage. Below it, a catalog long enough to make this modal
    // tall slides its header, and so the selection counter, under the navbar.
    <div className="tw-fixed tw-inset-0 tw-z-[1060] tw-flex tw-items-center tw-justify-center tw-bg-black/80">
      <div className="tw-bg-[#1E0039] tw-border tw-border-white/10 tw-w-full tw-max-w-2xl tw-rounded-2xl tw-shadow-2xl tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[90vh]">
        <div className="tw-flex tw-items-center tw-justify-between tw-p-6 tw-border-b tw-border-white/10 tw-flex-wrap tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
            <h3 className="tw-text-white tw-text-xl tw-font-bold tw-m-0">{t("Select Supporters")}</h3>
            <span className="tw-text-[#DDB7FF] tw-text-sm tw-font-medium">
              {/* Not named "count": that key is reserved by i18next for pluralization,
                  which would send the lookup to a "_other" suffixed key that does not
                  exist and silently fall back to the English string. */}
              {t("{{selected}} of {{max}} selected", { selected: currentSelected.length, max: MAX_SUPPORTERS })}
            </span>
          </div>
          <button onClick={onClose} className="tw-text-white/60 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-text-xl">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">
          <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-4">
            {catalog.map(supporter => {
              const isSelected = currentSelected.find(s => getSafeId(s) === getSafeId(supporter));
              const isDisabled = !isSelected && currentSelected.length >= MAX_SUPPORTERS;
              return (
                <div
                  key={getSafeId(supporter)}
                  onClick={() => !isDisabled && toggleSupporter(supporter)}
                  aria-disabled={isDisabled}
                  className={`kk-supporter-tile tw-relative tw-min-h-[120px] tw-rounded-xl tw-border-2 tw-p-4 tw-flex tw-items-center tw-justify-center tw-transition-colors ${
                    isSelected ? "tw-border-[#FF43A7] tw-bg-[#FF43A7]/10 tw-cursor-pointer" :
                    isDisabled ? "tw-opacity-40 tw-cursor-not-allowed tw-border-white/10 tw-bg-transparent" :
                    "tw-border-white/10 hover:tw-border-white/30 tw-bg-transparent tw-cursor-pointer"
                  }`}
                >
                  {supporter.logoKey ? (
                    <img
                      src={`${IMAGE_URL_PREFIX}/${supporter.logoKey}`}
                      alt={supporter.name}
                      className="tw-max-w-full tw-max-h-[80px] tw-object-contain"
                    />
                  ) : (
                    <span className="tw-text-white tw-text-sm tw-font-bold tw-text-center">
                      {supporter.name}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="tw-p-6 tw-border-t tw-border-white/10 tw-flex tw-justify-end tw-gap-4">
          <button onClick={onClose} className="tw-px-6 tw-py-2 tw-rounded-lg tw-bg-transparent tw-border tw-border-white/20 tw-text-white tw-font-bold tw-cursor-pointer hover:tw-bg-white/5">
            {t("Cancel")}
          </button>
          <button onClick={() => onConfirm(currentSelected)} className="tw-px-6 tw-py-2 tw-rounded-lg tw-bg-[#FF43A7] hover:tw-bg-[#e63c96] tw-border-none tw-text-white tw-font-bold tw-cursor-pointer">
            {t("Confirm")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default function EpkSupporters({ epkInfo, isEditMode, onChange }) {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const IMAGE_URL_PREFIX = `${process.env.REACT_APP_AWS_URL}`;

  const supportersList = (epkInfo?.supporters || []).filter(
    (s) => s && typeof s === 'object' && s.name
  );

  if (supportersList.length === 0 && !isEditMode) return null;

  const handleRemove = (supporter) => {
    const id = getSafeId(supporter);
    onChange && onChange('supporters', supportersList.filter((s) => getSafeId(s) !== id));
  };

  const handleConfirm = (chosen) => {
    onChange && onChange('supporters', chosen);
    setIsModalOpen(false);
  };

  return (
    <div className="tw-py-8 tw-mb-8 tw-bg-[#1E0039] tw-w-full">
      <div className="tw-relative tw-w-full tw-max-w-[1232px] tw-mx-auto tw-flex tw-flex-col tw-px-4 md:tw-px-0">
        <div className="tw-flex tw-flex-col tw-mb-8 md:tw-mb-10">
          <span className="tw-text-[#FF43A7] tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-mb-1">
            {t("With The Support Of")}
          </span>
          <h2 className="tw-text-white tw-text-3xl md:tw-text-4xl tw-font-bold tw-tracking-tight tw-mb-2">
            {isEditMode ? t("Edit Supporters") : t("Supporters")}
          </h2>
          {isEditMode && (
            <p className="tw-text-[#DDB7FF] tw-text-sm md:tw-text-base tw-leading-relaxed tw-max-w-[672px] tw-mt-2 tw-mb-0">
              {t("Choose the supporters to display on your EPK. The list is curated by KinoKlik, so you can only pick from approved supporters.")}
            </p>
          )}
        </div>

        <div className="tw-bg-white tw-w-full tw-rounded-[32px] md:tw-rounded-[40px] tw-py-8 md:tw-py-12 tw-px-4 md:tw-px-12">
          <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 tw-items-stretch">
            {isEditMode && (
              <ActionPlaceholder
                variant="photo"
                onClick={() => setIsModalOpen(true)}
                title={t("Add Supporter")}
                className="tw-min-h-[120px] md:tw-min-h-[140px]"
              />
            )}

            {supportersList.map((supporter) => {
              const id = getSafeId(supporter);
              const logo = supporter.logoKey ? (
                <img
                  src={`${IMAGE_URL_PREFIX}/${supporter.logoKey}`}
                  alt={supporter.name}
                  className="tw-max-w-full tw-max-h-[80px] md:tw-max-h-[100px] tw-object-contain"
                />
              ) : (
                <span className="tw-text-[#1F0439] tw-text-sm md:tw-text-base tw-font-bold tw-text-center tw-px-2">
                  {supporter.name}
                </span>
              );

              return (
                <div key={id} className="tw-relative tw-min-h-[120px] md:tw-min-h-[140px]">
                  <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-rounded-[12px] tw-border tw-border-[#1E0039]/20 tw-p-4">
                    {supporter.websiteUrl ? (
                      <a
                        href={supporter.websiteUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={supporter.name}
                        className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full"
                      >
                        {logo}
                      </a>
                    ) : (
                      logo
                    )}
                  </div>

                  {isEditMode && (
                    <button
                      onClick={() => handleRemove(supporter)}
                      aria-label={t("Remove supporter")}
                      className="tw-absolute -tw-top-2 -tw-right-2 tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-[#FF43A7] tw-border-none tw-text-white tw-text-xs tw-cursor-pointer tw-shadow-md"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <SupporterPickerModal
          selected={supportersList}
          onConfirm={handleConfirm}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
}
